import Vue from 'vue';
import * as Sentry from '@sentry/browser';
import { Loading, MessageBox, Notification } from 'element-ui';
import VueProgressiveImage from 'vue-progressive-image';
import infiniteScroll from 'vue-infinite-scroll';
import VueClipboard from 'vue-clipboard2';
import VueLazyLoad from 'vue-lazyload';
import vueScrollTo from 'vue-scrollto';
import { mask } from 'vue-the-mask';
import * as log from 'loglevel';
import VueSanitize from 'vue-sanitize';

import App from './App.vue';
import router from './router';
import store from './store/index';
import firebase from './config/firebase';

import mixins from './shared/mixins';
import './assets/scss/theme.scss';
import TimeFilter from './shared/timeFilter';
import HumanizedTimeFilter from './shared/humanizedTimeFilter';
import DateFilter from './shared/dateFilter';
import TruncateFilter from './shared/truncateFilter';
import PodcastDateFilter from './shared/podcastDateFilter';
import TextFilter from './shared/textFilter';
import AmountFilter from './shared/amountFilter';
import TaxFilter from './shared/taxFilter';
import Analytics from './shared/analytics';
import ButtonEventDirective from './directives/button-event';
import CheckVisibleViewportDirective from './directives/check-visible-viewport';

import elementComponents from './element-components';
import PictureComponent from './components/PictureComponent';
import SvgComponent from './components/SvgComponent';
import GridCol from './components/GridCol';

import axiosInterceptor from './shared/axiosInterceptor';
import 'url-search-params-polyfill';
import euCountries from './assets/json/eu_countries.json';

Object.defineProperty(Vue.prototype, '$analytics', { value: Analytics });
Vue.prototype.$eventHub = new Vue();

axiosInterceptor();

function initSentry() {
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    integrations: [new Sentry.Integrations.Vue()],
  });
}

function removeSplashScreen() {
  const loader = document.getElementById('loader');
  if (loader) {
    loader.classList.add('hiding');
    loader.style.zIndex = -1;
    setTimeout(() => {
      loader.style.display = 'none';
    }, 600);
  }
}

function enablePageScrolling() {
  const element = document.getElementsByTagName('html')[0];
  element.style.overflow = 'auto';
}

function checkIfTouchDevice() {
  const isTouch = 'ontouchstart' in window || navigator.msMaxTouchPoints;
  if (isTouch) {
    const element = document.getElementsByTagName('html')[0];
    element.classList.add('isTouch');
    window.isTouch = true;
  }
}

const initLogger = () => {
  log.setLevel(log.levels.TRACE);

  if (process.env.NODE_ENV === 'production') {
    log.setLevel(log.levels.ERROR); // Log only ERROR level logs in production. Can be set to SILENT to disable all logs completely.
  }

  log.info('Logger Initialized.');
};

elementComponents.forEach((component) => {
  Vue.component(component.name, component);
});
Vue.component('picture-component', PictureComponent);
Vue.component('svg-component', SvgComponent);
Vue.component('grid-col', GridCol);

Vue.use(VueProgressiveImage);
Vue.use(infiniteScroll);
Vue.use(VueClipboard);
Vue.use(Loading.directive);
Vue.use(vueScrollTo, {
  offset: -50,
});
Vue.use(VueLazyLoad);
Vue.use(VueSanitize, {
  allowedTags: VueSanitize.defaults.allowedTags.concat(['img']),
});

Vue.prototype.$loading = Loading.service;
Vue.prototype.$confirm = MessageBox.confirm;
Vue.prototype.$notify = Notification;

Vue.mixin(mixins);

Vue.filter('timeFormat', TimeFilter);
Vue.filter('humanizedTimeFilter', HumanizedTimeFilter);
Vue.filter('dateFormat', DateFilter);
Vue.filter('truncate', TruncateFilter);
Vue.filter('podcastDateFilter', PodcastDateFilter);
Vue.filter('textFilter', TextFilter);
Vue.filter('amountFilter', AmountFilter);
Vue.filter('taxFilter', TaxFilter);

Vue.directive('button-event', ButtonEventDirective);
Vue.directive('check-visible-viewport', CheckVisibleViewportDirective);
Vue.directive('mask', mask);

window.eventBus = new Vue();

function inEU(country) {
  if (country) {
    return euCountries.find(euCountry => euCountry.code === country.toUpperCase());
  }
  return false;
}

function insertConsentMangerScript() {
  const urlParams = new URLSearchParams(window.location.search);
  const country = urlParams.get('country');

  const isEUCountry = inEU(country);

  const script = document.createElement('script');
  script.src = 'https://unpkg.com/@segment/consent-manager@1.2.0/standalone/consent-manager.js';
  script.crossOrigin = 'anonymous';
  script.defer = true;
  script.setAttribute('data-container', '#segment-consent-manager');
  script.setAttribute('data-writeKey', `${process.env.SEGMENT_KEY}`);
  script.setAttribute('data-implyConsentOnInteraction', isEUCountry ? 'false' : 'true');
  script.setAttribute('data-bannerContent', 'We use cookies (and other similar technologies) to collect data to improve your experience on our site.');
  script.setAttribute('data-bannerSubContent', 'You can change your preferences at any time.');
  script.setAttribute('data-preferencesDialogTitle', 'Website Data Collection Preferences');
  script.setAttribute('data-preferencesDialogContent', 'We use data collected by cookies and JavaScript libraries to improve your browsing experience, analyze site traffic, deliver personalized advertisements, and increase the overall performance of our site.');
  script.setAttribute('data-cancelDialogTitle', 'Are you sure you want to cancel?');
  script.setAttribute('data-cancelDialogContent', 'Your preferences have not been saved. By continuing to use our website, you՚re agreeing to our Website Data Collection Policy.');

  document.body.appendChild(script);
}

function insertOptimizelyScript() {
  const urlParams = new URLSearchParams(window.location.search);
  const country = urlParams.get('country');

  const isEUCountry = inEU(country);

  const script = document.createElement('script');
  script.src = 'https://cdn.optimizely.com/js/14235030109.js';
  script.defer = true;
  if (!isEUCountry) {
    document.getElementsByTagName('head')[0].appendChild(script);
  }
}
const unsubscribe = firebase.auth().onAuthStateChanged(() => {
  unsubscribe();

  removeSplashScreen();
  enablePageScrolling();
  checkIfTouchDevice();
  insertConsentMangerScript();
  insertOptimizelyScript();

  window.Vue = new Vue({
    el: '#app',
    router,
    store,
    render: h => h(App),
  });

  initSentry();
  initLogger();
});

// export default vueUserPortalInstance



// WEBPACK FOOTER //
// ./app/src/main.js